import React, { useState, useEffect } from "react";
import { useLocation, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Skeleton,
  Alert,
  Tabs,
  Tab,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
} from "@mui/material";
import {
  Videocam as StartIcon,
  Schedule as ScheduleIcon,
  List as ListIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Refresh as RefreshIcon,
  Event as EventIcon,
  StarBorder as StarIcon,
} from "@mui/icons-material";
import api from "../../api";
import ClassFeedbackSummaryDialog from "./ClassFeedbackSummaryDialog";
import GroupSelect from "../../components/GroupSelect";
import { useHostableSubjects } from "../../components/hostableSubjects";
import { tutorMeetingAppUrl } from "../../config/jitsi";
import { classStatusChip as statusConfig } from "./tutorStatusChips";

const formatDateTime = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
};

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

export default function TutorClasses() {
  const [courses, setCourses] = useState([]);
  const [classrooms, setClassrooms] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(true);
  const [loadingClassrooms, setLoadingClassrooms] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [tab, setTab] = useState(0);
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get("tab");
    if (tabParam !== null) {
      setTab(Number(tabParam));
    }
  }, [location.search]);

  // Start class
  const [startCourseId, setStartCourseId] = useState("");
  const [startSubjectId, setStartSubjectId] = useState("");
  const [startSubjects, setStartSubjects] = useState([]);
  // "" = whole course; otherwise only that group's students see the class.
  const [startPlanId, setStartPlanId] = useState("");
  // The group object behind it, so the subject picker can honour a subject-wise split.
  const [startGroup, setStartGroup] = useState(null);
  const [loadingStartSubjects, setLoadingStartSubjects] = useState(false);
  const [creating, setCreating] = useState(false);

  // Schedule class
  const [scheduleCourseId, setScheduleCourseId] = useState("");
  const [scheduleSubjectId, setScheduleSubjectId] = useState("");
  const [scheduleSubjects, setScheduleSubjects] = useState([]);
  const [schedulePlanId, setSchedulePlanId] = useState("");
  const [scheduleGroup, setScheduleGroup] = useState(null);
  const [loadingScheduleSubjects, setLoadingScheduleSubjects] = useState(false);
  const [scheduleName, setScheduleName] = useState("");
  const [scheduleStart, setScheduleStart] = useState("");
  const [scheduleEnd, setScheduleEnd] = useState("");
  const [scheduling, setScheduling] = useState(false);
  const [endingMeetingId, setEndingMeetingId] = useState(null);
  const [feedbackFor, setFeedbackFor] = useState(null);
  const [startingMeetingId, setStartingMeetingId] = useState(null);
  const [classFilter, setClassFilter] = useState("all"); // all | pending | ongoing | ended

  // Where a group is split by subject, a tutor only starts/schedules the subjects assigned to them
  // in it. A whole-course class (no group) keeps the full list.
  const hostableStartSubjects = useHostableSubjects(
    startSubjects,
    startGroup,
    startSubjectId,
    (id) => setStartSubjectId(id === "" ? "" : String(id)),
    { autoSelect: "first" }
  );
  const hostableScheduleSubjects = useHostableSubjects(
    scheduleSubjects,
    scheduleGroup,
    scheduleSubjectId,
    (id) => setScheduleSubjectId(id === "" ? "" : String(id)),
    { autoSelect: "first" }
  );

  const statusOrder = { pending: 0, ongoing: 1, ended: 2 };
  const sortedClassrooms = [...classrooms].sort((a, b) => {
    const oa = statusOrder[a.status] ?? 3;
    const ob = statusOrder[b.status] ?? 3;
    if (oa !== ob) return oa - ob;
    return (a.start_time || "").localeCompare(b.start_time || "");
  });
  const filteredClassrooms = classFilter === "all"
    ? sortedClassrooms
    : sortedClassrooms.filter((c) => c.status === classFilter);

  const fetchCourses = async () => {
    setLoadingCourses(true);
    try {
      const res = await api.get("tutor/courses");
      if (res.data?.success && Array.isArray(res.data.data)) {
        setCourses(res.data.data);
        if (res.data.data.length && !startCourseId) setStartCourseId(String(res.data.data[0].id));
        if (res.data.data.length && !scheduleCourseId) setScheduleCourseId(String(res.data.data[0].id));
      }
    } catch (e) {
      setError(e.response?.data?.message || "Failed to load courses");
    } finally {
      setLoadingCourses(false);
    }
  };

  const fetchClassrooms = async () => {
    setLoadingClassrooms(true);
    try {
      const res = await api.get("tutor/classrooms");
      if (res.data?.success && Array.isArray(res.data.data)) {
        setClassrooms(res.data.data);
      }
    } catch (e) {
      setError(e.response?.data?.message || "Failed to load classes");
    } finally {
      setLoadingClassrooms(false);
    }
  };

  useEffect(() => {
    fetchCourses();
  }, []);

  // Load available subjects for the selected course (start meeting tab).
  useEffect(() => {
    if (!startCourseId) return;

    let cancelled = false;
    setStartSubjectId("");
    setStartSubjects([]);
    setLoadingStartSubjects(true);

    api
      .get(`tutor/courses/${encodeURIComponent(startCourseId)}/subjects`)
      .then((res) => {
        if (cancelled) return;
        const list = res.data?.success && Array.isArray(res.data.data) ? res.data.data : [];
        // The selection is seeded from the hostable list by useHostableSubjects, not from this
        // one — a group split by subject may not include the course's first subject.
        setStartSubjects(list);
      })
      .catch(() => {
        if (cancelled) return;
        setStartSubjects([]);
        setStartSubjectId("");
      })
      .finally(() => {
        if (cancelled) return;
        setLoadingStartSubjects(false);
      });

    return () => {
      cancelled = true;
    };
  }, [startCourseId]);

  // Load available subjects for the selected course (schedule meeting tab).
  useEffect(() => {
    if (!scheduleCourseId) return;

    let cancelled = false;
    setScheduleSubjectId("");
    setScheduleSubjects([]);
    setLoadingScheduleSubjects(true);

    api
      .get(`tutor/courses/${encodeURIComponent(scheduleCourseId)}/subjects`)
      .then((res) => {
        if (cancelled) return;
        const list = res.data?.success && Array.isArray(res.data.data) ? res.data.data : [];
        setScheduleSubjects(list);
      })
      .catch(() => {
        if (cancelled) return;
        setScheduleSubjects([]);
        setScheduleSubjectId("");
      })
      .finally(() => {
        if (cancelled) return;
        setLoadingScheduleSubjects(false);
      });

    return () => {
      cancelled = true;
    };
  }, [scheduleCourseId]);

  useEffect(() => {
    if (tab === 2) fetchClassrooms();
  }, [tab]);

  const handleStartMeeting = async () => {
    if (!startCourseId || !startSubjectId) {
      setError("Select a course and subject.");
      return;
    }
    const course = courses.find((c) => String(c.id) === String(startCourseId));
    const subject = startSubjects.find((s) => String(s.id) === String(startSubjectId));
    setCreating(true);
    setError(null);
    try {
      const res = await api.post("tutor/classrooms", {
        course_id: Number(startCourseId),
        subject_id: Number(startSubjectId),
        // Omitted when whole-course, so a course-wide class stays a deliberate choice.
        ...(startPlanId ? { course_plan_id: Number(startPlanId) } : {}),
        name: course && subject ? `${course.name} - ${subject.name}` : "Live class",
      });
      const roomCode = res.data?.classroom?.room_code;
      if (roomCode) {
        window.open(tutorMeetingAppUrl(roomCode), "_blank", "noopener,noreferrer");
        setSuccess("Meeting opened in a new tab. Recording starts automatically for hosts. Students join from Classes (no record).");
      }
    } catch (e) {
      setError(e.response?.data?.message || "Failed to create meeting");
    } finally {
      setCreating(false);
    }
  };

  const handleScheduleClass = async (e) => {
    e.preventDefault();
    if (!scheduleCourseId || !scheduleSubjectId || !scheduleName.trim()) {
      setError("Course, subject, and class name are required.");
      return;
    }
    setScheduling(true);
    setError(null);
    setSuccess(null);
    try {
      // Send times in UTC so backend stores correctly; datetime-local gives local time without timezone
      const startUtc = scheduleStart ? new Date(scheduleStart).toISOString() : null;
      const endUtc = scheduleEnd ? new Date(scheduleEnd).toISOString() : null;
      await api.post("tutor/classrooms", {
        course_id: Number(scheduleCourseId),
        subject_id: Number(scheduleSubjectId),
        ...(schedulePlanId ? { course_plan_id: Number(schedulePlanId) } : {}),
        name: scheduleName.trim(),
        start_time: startUtc,
        end_time: endUtc,
      });
      setSuccess("Class scheduled.");
      setScheduleName("");
      setScheduleStart("");
      setScheduleEnd("");
      fetchClassrooms();
    } catch (e) {
      setError(e.response?.data?.message || "Failed to schedule class");
    } finally {
      setScheduling(false);
    }
  };

  const handleStartExisting = async (classroom) => {
    setStartingMeetingId(classroom.id);
    setError(null);
    try {
      await api.post(`tutor/classrooms/${classroom.id}/start`);
      window.open(tutorMeetingAppUrl(classroom.room_code), "_blank", "noopener,noreferrer");
      fetchClassrooms();
    } catch (e) {
      setError(e.response?.data?.message || "Failed to start meeting");
    } finally {
      setStartingMeetingId(null);
    }
  };

  const handleEndMeeting = async (classroomId) => {
    setEndingMeetingId(classroomId);
    setError(null);
    try {
      await api.post(`tutor/classrooms/${classroomId}/end`);
      setSuccess("Meeting ended. End time recorded.");
      fetchClassrooms();
    } catch (e) {
      setError(e.response?.data?.message || "Failed to end meeting");
    } finally {
      setEndingMeetingId(null);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ sm: "center" }}
        justifyContent="space-between"
        spacing={1}
        sx={{ mb: 1 }}
      >
        <Typography variant="h5" fontWeight={700}>
          Classes
        </Typography>
        <Button variant="contained" startIcon={<EventIcon />} component={RouterLink} to="/tutor/classes/schedule">
          Schedule group class
        </Button>
      </Stack>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        Start a live meeting, schedule a class, or view and start from your scheduled classes.
        A <strong>group class</strong> is seen only by that group's students, who are notified when
        you schedule it; a class scheduled below without a group is visible to everyone on the course.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: "divider", mb: 0 }}>
        <Tab icon={<StartIcon />} iconPosition="start" label="Start class" />
        <Tab icon={<ScheduleIcon />} iconPosition="start" label="Schedule class" />
        <Tab icon={<ListIcon />} iconPosition="start" label="My scheduled classes" />
      </Tabs>

      <TabPanel value={tab} index={0}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
              Create and start a meeting now
            </Typography>
            <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel>Course</InputLabel>
                <Select
                  value={startCourseId}
                  label="Course"
                  onChange={(e) => setStartCourseId(e.target.value)}
                  disabled={loadingCourses}
                >
                  {courses.map((c) => (
                    <MenuItem key={c.id} value={String(c.id)}>{c.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 220 }} required>
                <InputLabel>Subject</InputLabel>
                <Select
                  value={startSubjectId}
                  label="Subject"
                  onChange={(e) => setStartSubjectId(e.target.value)}
                  disabled={loadingStartSubjects || hostableStartSubjects.length === 0}
                >
                  {hostableStartSubjects.map((s) => (
                    <MenuItem key={s.id} value={String(s.id)}>{s.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box sx={{ minWidth: 240 }}>
                <GroupSelect
                  value={startPlanId}
                  onChange={(id) => {
                    setStartPlanId(id);
                    // A subject the new group's tutor does not cover must not stay selected.
                    setStartSubjectId("");
                  }}
                  onGroupLoaded={setStartGroup}
                  courseId={startCourseId}
                />
              </Box>
              <Button
                variant="contained"
                startIcon={<PlayIcon />}
                onClick={handleStartMeeting}
                disabled={creating || loadingCourses || loadingStartSubjects}
                sx={{ background: "linear-gradient(90deg, #3B2A9F 0%, #D62926 100%)", "&:hover": { opacity: 0.9 } }}
              >
                {creating ? "Creating…" : "Create & start meeting"}
              </Button>
            </Box>
          </CardContent>
        </Card>
        <Typography variant="body2" color="text.secondary">
          Meeting will open in a new tab when you click &quot;Create &amp; start meeting&quot;.
        </Typography>
      </TabPanel>

      <TabPanel value={tab} index={1}>
        <Card>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
              Schedule a class with start and end time
            </Typography>
            <form onSubmit={handleScheduleClass}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 480 }}>
                <FormControl size="small" required>
                  <InputLabel>Course</InputLabel>
                  <Select
                    value={scheduleCourseId}
                    label="Course"
                    onChange={(e) => setScheduleCourseId(e.target.value)}
                    disabled={loadingCourses}
                  >
                    {courses.map((c) => (
                      <MenuItem key={c.id} value={String(c.id)}>{c.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" required>
                  <InputLabel>Subject</InputLabel>
                  <Select
                    value={scheduleSubjectId}
                    label="Subject"
                    onChange={(e) => setScheduleSubjectId(e.target.value)}
                    disabled={loadingScheduleSubjects || hostableScheduleSubjects.length === 0}
                  >
                    {hostableScheduleSubjects.map((s) => (
                      <MenuItem key={s.id} value={String(s.id)}>{s.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <GroupSelect
                  value={schedulePlanId}
                  onChange={(id) => {
                    setSchedulePlanId(id);
                    setScheduleSubjectId("");
                  }}
                  onGroupLoaded={setScheduleGroup}
                  courseId={scheduleCourseId}
                />
                <TextField
                  size="small"
                  label="Class name"
                  value={scheduleName}
                  onChange={(e) => setScheduleName(e.target.value)}
                  required
                  placeholder="e.g. Week 3 - Algebra"
                />
                <TextField
                  size="small"
                  label="Start time"
                  type="datetime-local"
                  value={scheduleStart}
                  onChange={(e) => setScheduleStart(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  size="small"
                  label="End time"
                  type="datetime-local"
                  value={scheduleEnd}
                  onChange={(e) => setScheduleEnd(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  disabled={scheduling || loadingCourses || loadingScheduleSubjects}
                  sx={{ alignSelf: "flex-start", background: "linear-gradient(90deg, #3B2A9F 0%, #D62926 100%)", "&:hover": { opacity: 0.9 } }}
                >
                  {scheduling ? "Scheduling…" : "Schedule class"}
                </Button>
              </Box>
            </form>
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={tab} index={2}>
        {loadingClassrooms ? (
          <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
        ) : classrooms.length === 0 ? (
          <Card><CardContent><Typography color="text.secondary">No scheduled classes yet. Create one from "Start class" or "Schedule class".</Typography></CardContent></Card>
        ) : (
          <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2, flexWrap: "wrap" }}>
              <ToggleButtonGroup
                value={classFilter}
                exclusive
                onChange={(_, v) => v != null && setClassFilter(v)}
                size="small"
                sx={{
                  "& .MuiToggleButton-root": {
                    textTransform: "none",
                    fontWeight: 600,
                  },
                }}
              >
                <ToggleButton value="all">All</ToggleButton>
                <ToggleButton value="pending">Pending</ToggleButton>
                <ToggleButton value="ongoing">Ongoing</ToggleButton>
                <ToggleButton value="ended">Ended</ToggleButton>
              </ToggleButtonGroup>
              <Button
                startIcon={<RefreshIcon />}
                onClick={fetchClassrooms}
                disabled={loadingClassrooms}
                size="small"
                variant="outlined"
              >
                Refresh
              </Button>
            </Box>
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "grey.100" }}>
                    <TableCell><strong>Class</strong></TableCell>
                    <TableCell><strong>Course</strong></TableCell>
                    <TableCell><strong>Group</strong></TableCell>
                    <TableCell><strong>Subject</strong></TableCell>
                    <TableCell><strong>Start</strong></TableCell>
                    <TableCell><strong>End</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell align="right"><strong>Action</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredClassrooms.map((c) => {
                  const config = statusConfig[c.status] || statusConfig.pending;
                  return (
                    <TableRow key={c.id}>
                      <TableCell>{c.name}</TableCell>
                      <TableCell>{c.course?.name ?? "—"}</TableCell>
                      <TableCell>
                        {/* No group means a legacy course-wide class — every student on the
                            course sees it, which is worth showing plainly. */}
                        {c.group?.name ? (
                          <Chip size="small" label={c.group.name} sx={{ fontWeight: 600 }} />
                        ) : (
                          <Chip size="small" variant="outlined" label="Whole course" />
                        )}
                      </TableCell>
                      <TableCell>{c.subject?.name ?? "—"}</TableCell>
                      <TableCell>{formatDateTime(c.start_time)}</TableCell>
                      <TableCell>{formatDateTime(c.end_time)}</TableCell>
                      <TableCell><Chip size="small" label={config.label} sx={{ bgcolor: config.color + "20", color: config.color }} /></TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", flexWrap: "wrap" }}>
                          {(c.status === "pending" || c.status === "ongoing") && (
                            <Button
                              size="small"
                              variant="contained"
                              startIcon={<PlayIcon />}
                              onClick={() => handleStartExisting(c)}
                              disabled={startingMeetingId === c.id}
                              sx={{ background: "linear-gradient(90deg, #3B2A9F 0%, #D62926 100%)", "&:hover": { opacity: 0.9 } }}
                            >
                              {startingMeetingId === c.id ? "Starting…" : "Start"}
                            </Button>
                          )}
                          {c.status === "ongoing" && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={<StopIcon />}
                              onClick={() => handleEndMeeting(c.id)}
                              disabled={endingMeetingId === c.id}
                              sx={{ borderColor: "#d32f2f", color: "#d32f2f", "&:hover": { borderColor: "#b71c1c", bgcolor: "rgba(211,47,47,0.04)" } }}
                            >
                              {endingMeetingId === c.id ? "Ending…" : "End meeting"}
                            </Button>
                          )}
                          {c.status === "ended" && (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<StarIcon />}
                              onClick={() => setFeedbackFor(c)}
                            >
                              Feedback
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            {filteredClassrooms.length === 0 && classFilter !== "all" && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                No {classFilter} classes.
              </Typography>
            )}
          </>
        )}
      </TabPanel>

      <ClassFeedbackSummaryDialog
        open={Boolean(feedbackFor)}
        classroom={feedbackFor}
        onClose={() => setFeedbackFor(null)}
      />
    </Box>
  );
}
